import { catchError, from, map, Observable, tap } from "rxjs";
import { ICachedItem, SecureStorageService, StoragePrefixType } from "../secure-storage/secure-storage-service";
import { Injectable } from "@angular/core";
import { ConnectivityService } from "../connectivity-service/connectivity-service";

export { StoragePrefixType };

@Injectable({ providedIn: 'root' })
export class NetworkService {

    isOnline$ = this.connectivity.connectivity$.pipe(map(s => s.connected));
    isOnline: boolean = true;

    constructor(private connectivity: ConnectivityService){
        this.isOnline$.subscribe(isOnline => {
            this.isOnline = isOnline;
        });
    }

    /**
     * Posts via API when online; on offline or API failure, queues the request for Offline sync.
     * Callers must pass a stable {@link storageId} (typically practitionerId_correlationId) and include
     * that same correlationId on the request so it is persisted in the cached payload.
     */
    performNetworkPostCacheFallbackCall<TReturnType, TRequestType>(
        prefix: StoragePrefixType,
        storageId: string,
        apiRequest: TRequestType,
        apiCall$: (request: TRequestType) => Observable<TReturnType>,
    ): Observable<TReturnType | null> {
        const cacheRequest = (): Observable<TReturnType | null> => {
            const cachedItem: ICachedItem<TRequestType> = {
                id: storageId,
                data: apiRequest,
                SyncFunctionRetryCount: 3
            };

            return from(
                SecureStorageService.saveWithPrefix<ICachedItem<TRequestType>>(storageId, cachedItem, prefix).then(
                    () => null,
                ),
            );
        };

        if (!this.isOnline) {
            return cacheRequest();
        }

        return apiCall$(apiRequest).pipe(catchError(() => cacheRequest()));
    }

    performNetworkCacheFallbackCall<T>(
        prefix: StoragePrefixType,
        storageId: string,
        apiCall$: Observable<T>
    ): Observable<T | null> {

        if (!this.isOnline) {
            return from(
                SecureStorageService.getWithPrefix<ICachedItem<T>>(storageId, prefix).then(cachedData =>{
                    return cachedData ? (cachedData.data as T) : null
                })
            );
        } else{
            return apiCall$.pipe(
                tap(async response => {
                    // Cache the response for offline use
                    await SecureStorageService.removeWithPrefix(storageId, prefix);
                    const cachedItem: ICachedItem<T> = {
                        id: storageId,
                        data: response
                    };
                   
                    await SecureStorageService.saveWithPrefix<ICachedItem<T>>(storageId, cachedItem, prefix);
                })
            )
        }
    }
}
